import { CloseOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { InputNumber, Tooltip } from 'antd';

import { useKpiAxisConfigurationBreakpoints } from 'components/molecules/PackArchitecture/useKpiAxisConfigurationBreakpoints';
import type { KpiDataRange } from 'components/molecules/PackArchitecture/kpiAxisConfiguration.utils';
import {
    handleKpiIntegerInputKeyDown,
    KPI_INTEGER_INPUT_PROPS,
} from 'components/molecules/PackArchitecture/kpiRangeSlider.utils';

type KpiAxisConfigurationBreakpointsFieldProps = {
    min: number;
    max: number;
    breakpoints: number[];
    dataRange: KpiDataRange;
    breakPointLabel: string;
    onBreakpointsChange: (breakpoints: number[]) => void;
    onRevertToUniform: () => void;
};

const BREAKPOINT_INPUT_CLASS =
    '!h-6 !min-w-0 !w-full !flex-1 !border-0 !shadow-none [&_.ant-input-number]:!h-6 [&_.ant-input-number]:!min-w-0 [&_.ant-input-number]:!w-full [&_.ant-input-number]:!flex-1 [&_.ant-input-number]:!border-0 [&_.ant-input-number]:!shadow-none [&_.ant-input-number-input-wrap]:!h-6 [&_.ant-input-number-input-wrap]:!w-full [&_.ant-input-number-input]:!h-6 [&_.ant-input-number-input]:!w-full [&_.ant-input-number-input]:!px-0 [&_.ant-input-number-input]:!text-sm';

const KpiAxisConfigurationBreakpointsField = ({
    min,
    max,
    breakpoints,
    dataRange,
    breakPointLabel,
    onBreakpointsChange,
    onRevertToUniform,
}: KpiAxisConfigurationBreakpointsFieldProps) => {
    const {
        sortedBreakpoints,
        isDraftOpen,
        draftInputValue,
        draftInvalid,
        isAtMaxBreakpoints,
        canAddBreakpoint,
        invalidIndex,
        scrollContainerRef,
        scrollState,
        hasError,
        handleScrollBreakpoints,
        handleRemoveBreakpoint,
        handleAddBreakpoint,
        handleCancelDraft,
        handleDraftInputChange,
        handleDraftKeyDown,
        handleDraftBlur,
        handleBreakpointChange,
    } = useKpiAxisConfigurationBreakpoints({
        min,
        max,
        breakpoints,
        dataRange,
        onBreakpointsChange,
        onRevertToUniform,
    });

    return (
        <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                    <span className="shrink-0 text-sm text-blackTransparent88">{breakPointLabel}:</span>
                    {scrollState.hasOverflow ? (
                        <button
                            type="button"
                            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded border bg-white ${
                                scrollState.canScrollLeft
                                    ? 'cursor-pointer border-borderGreyAlt1 text-toolbarLabelGrey hover:text-blackTitle'
                                    : 'cursor-not-allowed border-borderGreyAlt1 opacity-40'
                            }`}
                            disabled={!scrollState.canScrollLeft}
                            aria-label="Scroll breakpoints left"
                            onClick={() => handleScrollBreakpoints('left')}
                        >
                            <LeftOutlined className="text-[10px]" />
                        </button>
                    ) : null}
                    <div
                        ref={scrollContainerRef}
                        className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                    >
                        {sortedBreakpoints.map((breakpoint, index) => (
                            <div
                                key={`${breakpoint}-${index}`}
                                className={`kpi-breakpoint-chip inline-flex h-6 w-[68px] shrink-0 items-center overflow-hidden rounded border bg-white pl-2 ${
                                    invalidIndex === index ? 'border-redAlt' : 'border-borderGreyAl1'
                                }`}
                            >
                                <InputNumber
                                    size="small"
                                    controls={false}
                                    className={BREAKPOINT_INPUT_CLASS}
                                    value={breakpoint}
                                    {...KPI_INTEGER_INPUT_PROPS}
                                    onKeyDown={handleKpiIntegerInputKeyDown}
                                    onChange={(value) => handleBreakpointChange(index, value)}
                                />
                                <button
                                    type="button"
                                    className="flex shrink-0 items-center justify-center text-toolbarLabelGrey pt-1 pr-1 hover:text-blackTitle"
                                    aria-label={`Remove breakpoint ${breakpoint}`}
                                    onClick={() => handleRemoveBreakpoint(index)}
                                >
                                    <CloseOutlined className="text-[10px]" />
                                </button>
                            </div>
                        ))}
                        {isDraftOpen ? (
                            <div
                                className={`kpi-breakpoint-chip inline-flex h-6 w-[68px] shrink-0 items-center overflow-hidden rounded border bg-white pl-2 ${
                                    draftInvalid ? 'border-redAlt' : 'border-borderGreyAl1'
                                }`}
                            >
                                <InputNumber
                                    size="small"
                                    controls={false}
                                    autoFocus
                                    className={BREAKPOINT_INPUT_CLASS}
                                    value={draftInputValue === '' ? null : Number(draftInputValue)}
                                    {...KPI_INTEGER_INPUT_PROPS}
                                    onKeyDown={handleDraftKeyDown}
                                    onChange={handleDraftInputChange}
                                    onBlur={handleDraftBlur}
                                />
                                <button
                                    type="button"
                                    className="flex shrink-0 items-center justify-center text-toolbarLabelGrey pt-1 pr-1 hover:text-blackTitle"
                                    aria-label="Cancel new breakpoint"
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={handleCancelDraft}
                                >
                                    <CloseOutlined className="text-[10px]" />
                                </button>
                            </div>
                        ) : null}
                    </div>
                    {scrollState.hasOverflow ? (
                        <button
                            type="button"
                            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded border bg-white ${
                                scrollState.canScrollRight
                                    ? 'cursor-pointer border-borderGreyAlt1 text-toolbarLabelGrey hover:text-blackTitle'
                                    : 'cursor-not-allowed border-borderGreyAlt1 opacity-40'
                            }`}
                            disabled={!scrollState.canScrollRight}
                            aria-label="Scroll breakpoints right"
                            onClick={() => handleScrollBreakpoints('right')}
                        >
                            <RightOutlined className="text-[10px]" />
                        </button>
                    ) : null}
                    <Tooltip
                        title={
                            isAtMaxBreakpoints ? 'Maximum 10 breakpoints can be added.' : undefined
                        }
                    >
                        <button
                            type="button"
                            className={`shrink-0 border-0 bg-transparent p-0 text-sm font-medium ${
                                canAddBreakpoint
                                    ? 'cursor-pointer text-purpleFont'
                                    : 'cursor-not-allowed text-toolbarLabelGrey opacity-50'
                            }`}
                            disabled={!canAddBreakpoint}
                            onClick={handleAddBreakpoint}
                        >
                            +Add
                        </button>
                    </Tooltip>
                </div>
                <p className={`mb-0 mt-1 text-xs text-redAlt ${hasError ? 'visible' : 'invisible'}`}>
                    Breakpoint must be between Min and Max
                </p>
            </div>
        </div>
    );
};

export default KpiAxisConfigurationBreakpointsField;
